// Referral stage 1 (LB-370, Alan, 24 Sep) — the client side of a household's own invite
// link. Everything shared by the two surfaces lives here so Settings → Household and the
// parent Home card cannot drift apart: the link shape, the share message, the share act,
// and the two questions the Home card asks the household ("has a child used it yet?",
// "has this household shared already?").
import { apiMintReferral } from "./api.js";
import { t as ct } from "./content.js";
import { publicOrigin } from "./origin.js";
import { shareText } from "./share.js";

// LB-361's link shape, from the app's own origin: /?invite=CODE&utm_*. utm_source is
// `referral`, which is how a household link and an admin link (utm_source=invite,
// worker/handlers/admin.js inviteShareLink) tell apart in Analytics → Acquisition.
export function referralShareLink(inviteCode) {
  const origin = publicOrigin();
  const params = new URLSearchParams({ invite: inviteCode, utm_source: "referral", utm_medium: "share", utm_campaign: "household" });
  return `${origin}/?${params.toString()}`;
}

// Alan's wording (24 Sep). Through ct() so Content Studio can change it. The link is
// appended on its own line, never interpolated into the sentence, so a messaging app
// unfurls it cleanly.
export function referralShareText(inviteCode) {
  const body = ct("referral.share_text", "I've been using Little Bankers with our kids. They earn their money by helping out at home and doing a bit of learning, and the app does all the tracking for me and saves the arguments. Worth a look:");
  return `${body}\n${referralShareLink(inviteCode)}`;
}

// Mint-or-get, then share. Returns "shared" | "copied" | "dismissed" | { error }.
// - share sheet where the browser has one (navigator.share), the clipboard everywhere else;
// - a dismissed share sheet is not a failure and gets no toast (same rule as shareTicket);
// - "shared" and "copied" both count as the household having shared, which is what retires
//   the Home card (markReferralShared below).
export async function shareReferral(code, { mode = "share" } = {}) {
  const res = await apiMintReferral(code);
  if (!res.ok) return { error: res.networkError ? "network" : res.error || "refused", status: res.status || 0 };
  const text = referralShareText(res.inviteCode);
  const link = referralShareLink(res.inviteCode);
  // Native Run C (LB-392): share mode goes through src/lib/share.js (the app's sheet, the
  // browser's, the clipboard); copy mode is the bare link on the clipboard, as before.
  if (mode === "share") return shareText(text);
  try {
    await navigator.clipboard.writeText(link);
    return "copied";
  } catch (err) {
    if (err && err.name === "AbortError") return "dismissed";
    return { error: "copy" };
  }
}

// The Home card's first question. "A child has used it" is the lifecycle engine's own
// definition of child activity (worker/lib/lifecycle-milestones.js childActivityTimes,
// LB-316): a habit tick, an earning credit (earnCap or learnEarnKind, never a parent
// top-up) or a request a child raised. Kid Mode visits themselves live only in Analytics,
// which the client cannot read, so this is the nearest fact the state holds and it is the
// same one "your kids haven't started" mails on. Kept in step by scripts/check-referral.mjs.
export function childHasUsedApp(state) {
  if (!state) return false;
  if ((state.habitLog || []).some((l) => l && l.childId)) return true;
  if ((state.requests || []).some((r) => r && r.childId)) return true;
  return (state.transactions || []).some((t) => t && t.type === "credit" && t.childId && (t.earnCap || t.learnEarnKind));
}

// The Home card's second question, and the act that answers it. settings.referralSharedAt
// is set the first time this household shares or copies its link (Alan, 24 Sep: the card
// "stays until the parent has shared or copied once, then Settings only"). Household-level
// on purpose — a second adult on another device should not see a card the first already
// acted on. No dismiss cross, by decision.
export function referralShared(state) {
  return !!(state && state.settings && state.settings.referralSharedAt);
}

export function showReferralHomeCard(state) {
  return !!(state && state.onboardingDone) && childHasUsedApp(state) && !referralShared(state);
}

// Not written to settingsLog: that ring is for settings that move money or security
// (SettingsTab LOG_LABELS), and sharing a link is neither.
export function markReferralShared(update) {
  update((s) => {
    if (s.settings.referralSharedAt) return null;
    s.settings.referralSharedAt = new Date().toISOString();
    return s;
  });
}
